import { useEffect, useRef, useState } from "react";
import { graphql } from "../../__generated__/gql";
import { useMutation, useQuery } from "urql";
import Button from "../../ui/Button/Button";
import { ArrowRight } from "../../ui/Icon/Icon";
import styles from "./KeywordSuggestion.module.scss";
import Card from "../../ui/Card/Card";
import Label from "../../ui/Label/Label";
import { Controller, useForm } from "react-hook-form";
import TextInput from "../../ui/TextInput/TextInput";
import { Transition, TransitionStatus } from "react-transition-group";
import classNames from "classnames";

type CustomKeywordFormData = {
  keyword: string;
};

function CustomKeywordForm({
  ref,
  className,
  onCancel,
  onSubmit,
}: {
  ref: React.Ref<HTMLDivElement>;
  className?: string;
  onCancel: () => void;
  onSubmit: (data: CustomKeywordFormData) => void;
}) {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isSubmitted },
    watch,
  } = useForm<CustomKeywordFormData>();

  const keyword = watch("keyword");

  return (
    <Card className={classNames(styles.card, className)} ref={ref}>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.textInputStep}>
        <Label label="Keyword" htmlFor="keyword">
          <Controller
            control={control}
            name="keyword"
            render={({ field }) => (
              <TextInput {...field} placeholder="Enter a keyword" />
            )}
          />
        </Label>
        <div className={styles.cardButtons}>
          <Button
            onClick={onCancel}
            className={styles.button}
            variant="outline"
            size="small"
            disabled={isSubmitting || isSubmitted}
          >
            Back
          </Button>
          <Button
            className={styles.button}
            type="submit"
            variant="primary"
            size="small"
            loading={isSubmitting || isSubmitted}
            disabled={isSubmitting || isSubmitted || !keyword}
          >
            Continue
          </Button>
        </div>
      </form>
    </Card>
  );
}

const SuggestKeywordsQuery = graphql(/* GraphQL */ `
  query suggestKeywords($input: SuggestKeywordsInput!) {
    suggestKeywords(input: $input)
  }
`);

const CreateFirstArticleMutation = graphql(/* GraphQL */ `
  mutation createFirstArticle($keyword: String!) {
    generateKeywordArticles(input: { articles: [{ keyword: $keyword }] }) {
      ... on GenerateArticlesResult {
        count
      }
    }
  }
`);

function KeywordSuggestion({
  ref,
  state: outerState,
  setMessage,
  onNext,
}: {
  ref: React.Ref<HTMLDivElement>;
  state: TransitionStatus;
  setMessage: (message: string) => void;
  onNext: () => void;
}) {
  const [keywords, setKeywords] = useState<string[]>([]);
  const [isReady, setIsReady] = useState(false);
  const [isAddingCustomKeyword, setIsAddingCustomKeyword] = useState(false);

  const [suggestKeywordsResponse, refetchSuggestKeywords] = useQuery({
    query: SuggestKeywordsQuery,
    pause: keywords.length > 0,
    variables: {
      input: {
        excludeKeywords: keywords,
      },
    },
  });
  const [, createFirstArticle] = useMutation(CreateFirstArticleMutation);

  const onSubmit = async (keyword: string) => {
    await createFirstArticle({ keyword });
    onNext();
  };

  const getMoreKeywords = () => {
    refetchSuggestKeywords();
    setMessage("How about these keywords?");
  };

  const startWriteYourOwnFlow = () => {
    setIsAddingCustomKeyword(true);
    setMessage("Go ahead and add a keyword");
  };

  const cancelWriteYourOwnFlow = () => {
    setIsAddingCustomKeyword(false);
    setMessage("How about these keywords?");
  };

  useEffect(() => {
    if (
      suggestKeywordsResponse.data &&
      suggestKeywordsResponse.data.suggestKeywords.length
    ) {
      setKeywords((previousValue) => {
        const uniqueKeywords = new Set([
          ...previousValue,
          ...(suggestKeywordsResponse.data?.suggestKeywords ?? []),
        ]);

        return Array.from(uniqueKeywords);
      });
    }
  }, [suggestKeywordsResponse.data]);

  useEffect(() => {
    setMessage("Fantastic!");

    setTimeout(() => {
      setMessage("Okay, one last thing");
    }, 2000);

    setTimeout(() => {
      setMessage("Time to pick the first keyword for me to write about");
      setIsReady(true);
    }, 5000);
  }, [setMessage]);

  const addingKeywordRef = useRef<HTMLDivElement>(null);
  const notAddingKeywordRef = useRef<HTMLDivElement>(null);

  return (
    isReady && (
      <div ref={ref} style={{ width: "100%", height: "100%", display: "flex" }}>
        <Transition
          in={isAddingCustomKeyword}
          timeout={200}
          appear
          mountOnEnter
          unmountOnExit
          nodeRef={addingKeywordRef}
        >
          {(state) => (
            <CustomKeywordForm
              ref={addingKeywordRef}
              onCancel={cancelWriteYourOwnFlow}
              onSubmit={async (data) => onSubmit(data.keyword)}
              className={classNames({
                [styles.entering]:
                  state === "entering" || outerState === "entering",
                [styles.exiting]:
                  state === "exiting" || outerState === "exiting",
              })}
            />
          )}
        </Transition>

        <Transition
          in={!isAddingCustomKeyword}
          timeout={200}
          appear
          mountOnEnter
          unmountOnExit
          nodeRef={notAddingKeywordRef}
        >
          {(state) => (
            <div
              ref={notAddingKeywordRef}
              className={classNames(styles.keywordsSuggestion, {
                [styles.entering]:
                  state === "entering" || outerState === "entering",
                [styles.exiting]:
                  state === "exiting" || outerState === "exiting",
              })}
            >
              <div className={styles.keywords}>
                {keywords.map((keyword) => (
                  <Button
                    variant="unstyled"
                    className={styles.keywordButton}
                    onClick={() => onSubmit(keyword)}
                    key={keyword}
                  >
                    <span>{keyword}</span>
                    <ArrowRight className={styles.keywordArrow} size="large" />
                  </Button>
                ))}
              </div>
              <div className={styles.buttons}>
                <Button
                  onClick={() => startWriteYourOwnFlow()}
                  variant="outline"
                >
                  Write your own
                </Button>
                <Button
                  onClick={() => getMoreKeywords()}
                  variant="outline"
                  loading={suggestKeywordsResponse.fetching}
                  disabled={suggestKeywordsResponse.fetching}
                >
                  Generate more
                </Button>
              </div>
            </div>
          )}
        </Transition>
      </div>
    )
  );
}

export default KeywordSuggestion;
